/**
 * 項目バリデーション（#2815）関連の定数・ヘルパー。
 *
 * 読取パターンの各項目に「必須」「文字数（最小/最大）」「数値範囲（最小値/最大値）」の
 * 制約を設定できる。制約の種類はデータ型によって出し分ける。
 */

// データ型の内部値（constants/options.ts の dataTypeOptions の value と一致させる）
export const itemDataType = {
  string: 'string',
  textarea: 'textarea',
  number: 'number',
  date: 'date',
  array: 'array',
  radio: 'radio',
  select: 'select',
} as const;

export type ItemDataType = (typeof itemDataType)[keyof typeof itemDataType];

/**
 * 固定値（#3073）の入力欄に出す入力例（#3314）。
 *
 * 値が取得できなかった項目に入れる値のため、その項目のデータ型に沿った例を出す。
 * 文字列型の項目に「例: 0」と出ると数値しか入れられないように見えるため、型ごとに変える。
 * 読取パターンの項目編集と子パターンの上書きで同じ文言を使う。
 */
export const fixedValuePlaceholder = (dataType: string | undefined): string => {
  switch (dataType) {
    case itemDataType.number:
      return '例: 0';
    case itemDataType.date:
      return '例: 2025/01/01';
    default:
      return '例: 株式会社サンプル';
  }
};

/**
 * 選択肢（#3075）を設定するデータ型。
 * ラジオボタン・プルダウンは表示形式が違うだけで、データの持ち方は同じ。
 */
export const choiceDataTypes: string[] = [itemDataType.radio, itemDataType.select];

/**
 * 明細（複数行データ）の列では選べないデータ型（#3075）。
 *
 * ラジオボタンは選択肢の数だけ横幅を必要とし、明細テーブルの列幅に収まらない。
 * プルダウンはセル内に収まるため明細でも選べる。
 */
export const parentOnlyDataTypes: string[] = [itemDataType.radio];

// データ型が選択肢を持つか（ラジオボタン / プルダウン）
export const isChoiceDataType = (type?: string): boolean =>
  type !== undefined && choiceDataTypes.includes(type);

/**
 * 計算フィールド（#3145）を設定できるデータ型。
 *
 * 計算エンジンが返すのは数値または文字列のため、それを素直に格納できる型に限る。
 * 日付は出力形式（outputFormat）で整形する前提の型で、計算結果をそのまま入れると
 * 形式が崩れる。複数行データ（array）は親自身が値を持たない。
 *
 * 選択肢型は当初「候補から選ぶ性質と式から値を導出する性質が両立しない」として
 * 除外していたが、#3358 で対象に含めた。IF の判定結果（"OK" / "不一致"）を
 * 選択肢で受けると、画面に結果がそのまま表示され、「要注意にする選択肢」
 * （#3357）と組み合わせて人に知らせられる。計算結果が選択肢に無い場合は
 * 既存の候補外表示（#3075）に従う。
 */
export const calculatableDataTypes: string[] = [
  itemDataType.string,
  itemDataType.textarea,
  itemDataType.number,
  // 選択肢型（#3358）。IF の判定結果を選択肢で受け、「要注意にする選択肢」と
  // 組み合わせて合計チェックなどに使う（設計書 §5.10）
  itemDataType.radio,
  itemDataType.select,
];

/**
 * データ型が計算フィールドに対応しているか（#3145 / #3195）。
 *
 * 表示（項目設定モーダル）と保存（itemListConverter）の両方でこの判定を使い、
 * 型を切り替えたときに前の型の計算設定が残らないようにする。
 */
export const isCalculatableDataType = (type?: string): boolean =>
  type !== undefined && calculatableDataTypes.includes(type);

/**
 * 文字数制約（最小/最大文字数）を設定できるデータ型。
 * テキスト・テキストエリアが対象。
 */
export const lengthConstraintTypes: string[] = [itemDataType.string, itemDataType.textarea];

/**
 * 数値範囲制約（最小値/最大値）を設定できるデータ型。
 * 数字のみが対象。
 */
export const valueRangeConstraintTypes: string[] = [itemDataType.number];

/**
 * 「0 を空欄として扱う」（#3262）を設定できるデータ型。
 *
 * 数字のみが対象。テキストで文字列 "0" を空欄扱いしたい場面は無く、
 * 出しても設定項目が増えるだけになるため。
 */
export const zeroAsEmptyConstraintTypes: string[] = [itemDataType.number];

// データ型が文字数制約に対応しているか
export const supportsLengthConstraint = (type?: string): boolean =>
  type !== undefined && lengthConstraintTypes.includes(type);

// データ型が数値範囲制約に対応しているか
export const supportsValueRangeConstraint = (type?: string): boolean =>
  type !== undefined && valueRangeConstraintTypes.includes(type);

// データ型が「0 を空欄として扱う」に対応しているか（#3262）
export const supportsZeroAsEmpty = (type?: string): boolean =>
  type !== undefined && zeroAsEmptyConstraintTypes.includes(type);

/**
 * 文字種・入力パターン（正規表現）を設定できるデータ型（#3357）。
 * 値を文字列として持つテキスト・テキストエリアが対象。
 * 数字型は値が数値のため文字種の概念が合わず、日付型は出力形式で整形される。
 */
export const charTypeConstraintTypes: string[] = [itemDataType.string, itemDataType.textarea];

/** 日付範囲を設定できるデータ型（#3357） */
export const dateRangeConstraintTypes: string[] = [itemDataType.date];

// データ型が文字種制約に対応しているか（#3357）
export const supportsCharTypeConstraint = (type?: string): boolean =>
  type !== undefined && charTypeConstraintTypes.includes(type);

// データ型が入力パターン（正規表現）に対応しているか（#3357）。対象は文字種と同じ
export const supportsPatternConstraint = (type?: string): boolean =>
  supportsCharTypeConstraint(type);

// データ型が日付範囲に対応しているか（#3357）
export const supportsDateRangeConstraint = (type?: string): boolean =>
  type !== undefined && dateRangeConstraintTypes.includes(type);

/**
 * 「要注意にする選択肢」（#3357）を設定できるデータ型。
 * 値が候補で決まる選択肢型（ラジオ・プルダウン）のみ。
 */
export const supportsReviewChoice = (type?: string): boolean => isChoiceDataType(type);

// ===== 文字種・入力パターン・日付範囲（#3357）=====

/**
 * 文字種の制約（#3357）。
 *
 * Issue には「英字の大文字ありなし、記号ありなし、数字のありなし、全角英数字NG」と
 * 個別の可否で挙がっているが、チェックボックスで組み合わせると 16 通りになり
 * 設定者の意図が読めなくなる（「大文字なし・記号あり・数字なし」とは何か）。
 * よく使う組み合わせをプリセットで選ばせ、表現できないものは入力パターン
 * （正規表現）で書いてもらう。
 */
export const itemCharType = {
  /** 数字のみ。0詰めの伝票番号（000003591）をテキスト型のまま検証するのが主用途 */
  digits: 'digits',
  /** 半角英数字のみ */
  alphanumeric: 'alphanumeric',
  /** 半角英数字と記号のみ */
  alphanumericSymbol: 'alphanumericSymbol',
  /** 半角英大文字と数字のみ */
  upperAlphanumeric: 'upperAlphanumeric',
  /** 半角のみ（全角を含まない）。Issue の「全角英数字NG」に対応する */
  halfWidth: 'halfWidth',
  /** 記号を含まない */
  noSymbol: 'noSymbol',
} as const;

export type ItemCharType = (typeof itemCharType)[keyof typeof itemCharType];

/**
 * 文字種ごとの判定パターン。
 *
 * グローバルフラグは付けない（lastIndex を持ち回ると test() の結果が
 * 呼び出しごとに変わるため）。
 */
export const charTypePattern: Record<ItemCharType, RegExp> = {
  digits: /^[0-9]+$/,
  alphanumeric: /^[0-9A-Za-z]+$/,
  // 半角英数字＋ASCII の記号（スペースは含めない）
  alphanumericSymbol: /^[0-9A-Za-z!-/:-@[-`{-~]+$/,
  upperAlphanumeric: /^[0-9A-Z]+$/,
  // 印字可能な ASCII のみ。全角英数字だけでなく、かな・漢字・全角スペースも弾く
  halfWidth: /^[\x20-\x7E]+$/,
  // ASCII の記号を1文字も含まないこと（日本語や英数字は許す）
  noSymbol: /^[^!-/:-@[-`{-~]+$/,
};

/** 文字種プルダウンの選択肢（未設定＝制約なしは呼び出し側で先頭に足す） */
export const itemCharTypeOptions: { value: ItemCharType; option: string }[] = [
  { value: itemCharType.digits, option: '数字のみ' },
  { value: itemCharType.alphanumeric, option: '半角英数字のみ' },
  { value: itemCharType.alphanumericSymbol, option: '半角英数字と記号のみ' },
  { value: itemCharType.upperAlphanumeric, option: '半角英大文字と数字のみ' },
  { value: itemCharType.halfWidth, option: '半角のみ（全角を含まない）' },
  { value: itemCharType.noSymbol, option: '記号を含まない' },
];

/** 日付範囲（#3357）の単位 */
export const dateRangeUnit = {
  day: 'day',
  month: 'month',
  year: 'year',
} as const;

export type DateRangeUnit = (typeof dateRangeUnit)[keyof typeof dateRangeUnit];

/** 日付範囲の単位の表示名。選択肢とエラー文言の両方で使う */
export const dateRangeUnitLabel: Record<DateRangeUnit, string> = {
  day: '日',
  month: 'か月',
  year: '年',
};

/** 日付範囲の単位プルダウンの選択肢 */
export const dateRangeUnitOptions: { value: DateRangeUnit; option: string }[] = [
  { value: dateRangeUnit.day, option: dateRangeUnitLabel.day },
  { value: dateRangeUnit.month, option: dateRangeUnitLabel.month },
  { value: dateRangeUnit.year, option: dateRangeUnitLabel.year },
];

/**
 * 文字種のエラー文言に埋め込む言い回し。
 * 「〜で入力してください」に続けて自然に読めるよう、選択肢のラベルとは別に持つ
 * （選択肢の「半角のみ（全角を含まない）」をそのまま使うと文が冗長になる）。
 */
export const itemCharTypeMessage: Record<ItemCharType, string> = {
  digits: '数字のみ',
  alphanumeric: '半角英数字のみ',
  alphanumericSymbol: '半角英数字と記号のみ',
  upperAlphanumeric: '半角英大文字と数字のみ',
  halfWidth: '半角のみ',
  noSymbol: '記号を含まない形式',
};

/**
 * 入力パターン（正規表現）の最大文字数（ReDoS 対策）。
 * 計算フィールド（#3145）の CALCULATION_REGEX_MAX_LENGTH と同じ値を、
 * バリデーション側の定数として持つ（計算エンジンは functions/main で
 * バイト同一の制約があり、そちらの定数を参照させたくないため）。
 */
export const ITEM_VALIDATION_PATTERN_MAX_LENGTH = 200;

/** 検証対象の文字列の最大文字数（ReDoS 対策）。これを超える値は判定しない */
export const ITEM_VALIDATION_TEXT_MAX_LENGTH = 10000;

/** 日付範囲に指定できる数値の上限（1〜この値） */
export const DATE_RANGE_LIMIT_MAX = 100;

// バリデーション設定UIのラベル
export const itemValidationLabel = {
  required: '必須項目にする',
  minLength: '最小文字数',
  maxLength: '最大文字数',
  minValue: '最小値',
  maxValue: '最大値',
  // 0を空欄扱い（#3262）。必須項目と組み合わせて使う
  treatZeroAsEmpty: '0 を空欄として扱う',
  // 文字種・入力パターン・日付範囲（#3357）
  charType: '文字種制限',
  reviewChoice: '要注意にする選択肢',
  pattern: '入力パターン（正規表現）',
  patternMessage: '正規表現のエラー文言',
  dateRangeSection: '許可する日付の範囲',
  dateNoPast: '過去の日付を許可しない',
  dateNoFuture: '未来の日付を許可しない',
  datePastLimit: 'さかのぼれる上限',
  dateFutureLimit: '先に進める上限',
  // 読み取り精度の要注意（#3384）
  reviewLowConfidence: '読み取り精度が低いときに要注意にする',
} as const;

// バリデーション設定UIのヘルパーテキスト（モーダル内）
export const itemValidationHelperText = {
  lengthRange: '最小文字数は最大文字数以下にしてください',
  valueRange: '最小値は最大値以下にしてください',
  // #3262: 金額0円・数量0が正当にありうる帳票ではオフのままにしてもらう
  treatZeroAsEmpty: '金額0円・数量0が正当にありうる帳票ではオフにしてください',
  // #3357
  charType: '0詰めの番号はテキスト型のまま「数字のみ」で検証できます',
  // 要注意にする選択肢（#3357）。エラーではなく「人が見た方がよい」の印
  reviewChoice: 'チェックした選択肢が選ばれていたら要注意にします',
  // 「半角のみ」は漢字・かなも弾くため、会社名などに使うと意図と違う結果になる
  charTypeHalfWidth: '「半角のみ」は漢字・かなも対象外になります',
  // 例は入力欄の placeholder に出すため、ここでは繰り返さない
  pattern: '未入力の場合は検証しません',
  patternMessage: '未入力の場合は既定の文言を表示します',
  // 日付範囲は OCR を実行した日を基準に判定する（閲覧日ではない）
  dateRange: '読み取りを実行した日を基準に判定します',
  // 読み取り精度の要注意（#3384）。バリデーションや照合の要注意は止まらない
  reviewLowConfidence: 'オフにすると、AIの読み取りに自信がない値でも要注意にしません',
} as const;

// 選択肢設定UI（#3075）のラベル
export const itemChoiceLabel = {
  section: '選択肢設定',
  description: 'AI読取ONの場合、ここで設定した選択肢の中から値が選ばれます',
  label: '表示名',
  value: '出力値',
  labelPlaceholder: '例: 有効',
  valuePlaceholder: '未入力なら表示名を使用',
  addButton: '選択肢を追加',
  unselected: '未選択',
  // ラジオボタンは HTML の仕様上いちど選ぶと全解除できないため、
  // 未選択へ戻す手段を添える（#3075）。文言・配置は Google フォームに倣う
  clear: '選択を解除',
  // 選択肢定義を後から変更した場合に、既存の結果データが候補外になることがある。
  // 値を勝手に消さずに見せるためのラベル（#3075）
  outOfChoice: (value: string): string => `(候補外) ${value}`,
} as const;

// 選択肢設定（#3075）のエラーメッセージ
export const itemChoiceErrorMessage = {
  empty: '選択肢を1つ以上設定してください',
  emptyLabel: '選択肢の表示名を入力してください',
  duplicatedLabel: '選択肢の表示名が重複しています',
  duplicatedValue: '選択肢の出力値が重複しています',
  maxCount: (max: number): string => `選択肢は${max}件までです`,
  notConfigured: (itemName: string): string => `${itemName}の選択肢を設定してください`,
} as const;

// OCR結果画面のエラーメッセージ生成
export const itemValidationResultMessage = {
  required: '必須項目です',
  minLength: (min: number): string => `${min}文字以上で入力してください`,
  maxLength: (max: number): string => `${max}文字以内で入力してください`,
  minValue: (min: number): string => `${min}以上の値を入力してください`,
  maxValue: (max: number): string => `${max}以下の値を入力してください`,
  // 文字種（#3357）。どの文字種に反したかが分かるよう、設定と同じ言葉で返す
  charType: (charType: ItemCharType): string =>
    `${itemCharTypeMessage[charType]}で入力してください`,
  // 入力パターン（#3357）。設定者がエラー文言を書いていればそちらを優先する
  pattern: '入力形式が正しくありません',
  // 日付範囲（#3357）。基準日は OCR を実行した日
  dateNoPast: '読み取り日より前の日付は入力できません',
  dateNoFuture: '読み取り日より後の日付は入力できません',
  datePastLimit: (limit: number, unit: DateRangeUnit): string =>
    `読み取り日から${limit}${dateRangeUnitLabel[unit]}より前の日付は入力できません`,
  dateFutureLimit: (limit: number, unit: DateRangeUnit): string =>
    `読み取り日から${limit}${dateRangeUnitLabel[unit]}より先の日付は入力できません`,
} as const;
